import {inject, Injectable} from '@angular/core';

import {CmsApiService} from '../../../core/cms-api.service';
import {ClientConfig} from '../../../core/definitions/client-config';
import {ObjectTypeInfo} from '../../../core/definitions/object-type-info';


export interface InventoryReportItem {
  artifact_id: string;
  identification_number: string | null;
  designations: string[];
  title: string | null;
  count: number | null;
  description: string | null;
  motif_description: string | null;
  current_placement: string | null;
  current_placement_code: string | null;
  collection: string | null;
  owner: string | null;
  registered_at: string | null;
  registered_by: string | null;
  internal_information: string | null;
  salvage_priority_registered: boolean;
  thumbnail_dms_identifier: string | null;
  thumbnail_url: string | null;
}

export interface InventoryReportPage {
  items: InventoryReportItem[];
  page: number;
  rows: number;
  object_type_filter_supported?: boolean;
}

export interface DeletedInventoryReportItem extends Omit<InventoryReportItem,
  'salvage_priority_registered' | 'thumbnail_dms_identifier' | 'thumbnail_url' |
  'current_placement' | 'current_placement_code'> {
  object_type_id: string;
  deleted_at: string;
  deleted_by: string | null;
  deletion_reason: string | null;
}

export interface DeletedInventoryReportPage {
  items: DeletedInventoryReportItem[];
  page: number;
  rows: number;
}

export type InventoryReportSort =
  'collection' |
  'designation' |
  'identification_number' |
  'placement' |
  'registered_at' |
  'title';

export type InventoryReportSortDirection = 'asc' | 'desc';
export type DeletedInventoryReportSort = 'deleted_at' | 'identification_number' | 'registered_at';

export type InventoryReportExportColumn =
  'artifact_id' |
  'deleted_at' |
  'deleted_by' |
  'deletion_reason' |
  'collection' |
  'count' |
  'current_placement' |
  'description' |
  'designations' |
  'identification_number' |
  'internal_information' |
  'motif_description' |
  'owner' |
  'registered_at' |
  'registered_by' |
  'salvage_priority_registered' |
  'title';

export type InventoryReportKind = 'inventory' | 'deleted';

export interface InventoryReportGenerationRequest {
  from_date?: string;
  to_date?: string;
  columns: InventoryReportExportColumn[];
  description_max_length: number;
  sort?: InventoryReportSort | DeletedInventoryReportSort;
  direction?: InventoryReportSortDirection;
  filter: string;
  object_types: string[];
}

export interface InventoryReportGeneration {
  job_id: string;
  status: 'registered' | 'in_progress' | 'finished' | 'downloaded' | 'failed';
  registered_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  error_message: string | null;
  file_name: string | null;
  file_size: number | null;
  row_count: number | null;
  download_available: boolean;
  options: Record<string, unknown>;
}

export interface InventoryReportGenerationState {
  active: InventoryReportGeneration | null;
  latest: InventoryReportGeneration | null;
}

export interface InventoryReportGenerationCreated {
  job_id: string;
  created: boolean;
}

export interface InventoryReportDownload {
  job_id: string;
  file_name: string;
  url: string;
}

@Injectable({providedIn: 'root'})
export class InventoryReportApiService {
  private readonly cmsApi = inject(CmsApiService);

  async getObjectTypes(): Promise<ObjectTypeInfo[]> {
    const config: ClientConfig = await this.cmsApi.executeService(
      'setting/get_client_config', {}, 'GET', undefined, false, false);
    return config.OBJECT_TYPES;
  }

  getDeletedPage(
    fromDate: string,
    toDate: string,
    page: number,
    rows: number,
    filter: string,
    objectTypes: string[],
    sort: DeletedInventoryReportSort = 'deleted_at',
    direction: InventoryReportSortDirection = 'desc',
  ): Promise<DeletedInventoryReportPage> {
    return this.cmsApi.executeService(
      'report/inventory/deleted',
      {from_date: fromDate, to_date: toDate, page, rows,
        filter, object_types: objectTypes.join(','), sort, direction},
      'GET',
      undefined,
      false,
      false,
    );
  }

  getPage(
    rows: number,
    page: number,
    sort: InventoryReportSort,
    direction: InventoryReportSortDirection,
    filter: string,
    objectTypes: string[],
  ): Promise<InventoryReportPage> {
    return this.cmsApi.executeService(
      'report/inventory',
      {rows, page, sort, direction, filter, object_types: objectTypes.join(',')},
      'GET',
      undefined,
      false,
      false,
    );
  }

  createGeneration(
    request: InventoryReportGenerationRequest,
    kind: InventoryReportKind,
  ): Promise<InventoryReportGenerationCreated> {
    return this.cmsApi.executeService(
      this.generationPath(kind),
      request,
      'POST',
      'application/json',
      false,
      false,
    );
  }

  getGenerationState(kind: InventoryReportKind): Promise<InventoryReportGenerationState> {
    return this.cmsApi.executeService(
      this.generationPath(kind),
      {},
      'GET',
      undefined,
      false,
      false,
    );
  }

  getGeneration(jobId: string, kind: InventoryReportKind): Promise<InventoryReportGeneration> {
    return this.cmsApi.executeService(
      `${this.generationPath(kind)}/${jobId}`,
      {},
      'GET',
      undefined,
      false,
      false,
    );
  }

  getGenerationDownload(jobId: string, kind: InventoryReportKind): Promise<InventoryReportDownload> {
    return this.cmsApi.executeService(
      `${this.generationPath(kind)}/${jobId}/download`,
      {},
      'GET',
      undefined,
      false,
      false,
    );
  }

  private generationPath(kind: InventoryReportKind): string {
    return `report/inventory${kind === 'deleted' ? '/deleted' : ''}/generations`;
  }
}
